import Head from "next/head";
import Image from "next/image";
import Link from "next/link";

import EvelienAvatar from "@/assets/images/about-us/evelien-avatar.jpg";
import { cn } from "@/common/utils/css.utils";
import { serializeJsonLd } from "@/common/utils/json-ld.utils";
import Breadcrumb from "@/components/common/Breadcrumb";
import Canonical from "@/components/common/Canonical";
import HrefLang from "@/components/common/HrefLang";
import SEOComponent from "@/components/common/seo";
import { buildBrandStorySchemas } from "@/features/brand-story/brand-story.schema";
import type { BrandStory } from "@/features/brand-story/brand-story.types";
import { Heading } from "@whoppah/ui/cva/Heading";

export type BrandStoryPageProps = {
  story: BrandStory;
  /** The page path without locale prefix, e.g. `/brands/west-elm`. */
  path: string;
};

/**
 * An editorial page for a brand we have no landing page (and so no listings) for yet. It is
 * English only: the stories were written for the US launch. `SearchPage.server.ts` serves it
 * only where the catalog 404s, so the moment the brand gets a real landing page, that page wins.
 */
export const BrandStoryPage = ({ story, path }: BrandStoryPageProps) => {
  const [heroImage, ...galleryImages] = story.images;

  return (
    <>
      <SEOComponent
        title={story.seoTitle}
        description={story.seoDescription}
        image={heroImage?.src}
        imageAlt={heroImage?.alt}
        ogType="article"
      />
      <HrefLang urlMap={{ en: path }} />
      <Canonical />
      <Head>
        {buildBrandStorySchemas(story, path).map(schema => (
          <script
            key={String(schema["@type"])}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: serializeJsonLd(schema) }}
          />
        ))}
      </Head>

      <article className="w-full">
        <div className="mx-auto w-full max-w-[1200px] px-4 pt-4 md:px-8">
          <Breadcrumb
            items={[
              { label: "Brands", href: "/brands" },
              { label: story.name, href: "" },
            ]}
          />
        </div>

        <header className="mx-auto mt-4 w-full max-w-[1200px] px-4 md:px-8">
          <div className="grid overflow-hidden rounded-2xl bg-brand-500 text-white md:grid-cols-2">
            <div className="flex flex-col justify-center gap-y-4 p-6 md:p-10 lg:p-12">
              <span className="w-fit rounded-full bg-flavour-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-brand-700">
                Brand story
              </span>
              <Heading as="h1" size="h1" className="text-white">
                {story.heading}
              </Heading>
              <p className="text-lg leading-7 text-brand-50">
                {story.subheading}
              </p>
              <dl className="mt-2 grid grid-cols-2 gap-3">
                {story.facts.map(fact => (
                  <div
                    key={fact.label}
                    className="rounded-xl bg-brand-700/60 px-4 py-3"
                  >
                    <dt className="text-xs uppercase tracking-wide text-brand-100">
                      {fact.label}
                    </dt>
                    <dd className="mt-0.5 text-sm font-bold">{fact.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            {heroImage ? (
              <figure className="relative min-h-[260px] md:min-h-[480px]">
                <Image
                  src={heroImage.src}
                  alt={heroImage.alt}
                  fill
                  priority
                  sizes="(min-width: 768px) 600px, 100vw"
                  className="object-cover"
                />
                <ImageCredit
                  image={heroImage}
                  className="absolute bottom-0 right-0 bg-black/50 text-white"
                />
              </figure>
            ) : (
              <div
                aria-hidden="true"
                className="hidden items-center justify-center bg-brand-700 p-10 font-title text-[6rem] font-bold leading-none text-brand-400 md:flex"
              >
                {story.name.charAt(0)}
              </div>
            )}
          </div>
        </header>

        <div className="mx-auto grid w-full max-w-[1200px] gap-10 px-4 py-10 md:px-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-16">
          <div className="flex min-w-0 flex-col gap-y-10">
            <Byline />

            <p className="text-lg leading-8 text-black">{story.intro}</p>

            {story.sections.map(section => (
              <section key={section.question} className="flex flex-col gap-y-4">
                <Heading as="h2" size="h3" className="text-brand-700">
                  {section.question}
                </Heading>
                {section.body.map(paragraph => (
                  <p
                    key={paragraph}
                    className="text-base leading-7 text-grey-800"
                  >
                    {paragraph}
                  </p>
                ))}
              </section>
            ))}

            <blockquote className="rounded-2xl bg-attention-50 p-6 md:p-8">
              <p className="font-title text-xl font-bold leading-8 text-attention-800 md:text-2xl">
                &ldquo;{story.quote.text}&rdquo;
              </p>
              <footer className="mt-3 text-sm text-attention-800">
                {story.quote.author}, Co-Founder and CEO of Whoppah
              </footer>
            </blockquote>

            {galleryImages.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {galleryImages.map(image => (
                  <figure key={image.src} className="flex flex-col gap-y-2">
                    <div className="aspect-[4/3] relative overflow-hidden rounded-xl bg-grey-50">
                      <Image
                        src={image.src}
                        alt={image.alt}
                        fill
                        sizes="(min-width: 640px) 400px, 100vw"
                        className="object-cover"
                      />
                    </div>
                    <ImageCredit image={image} className="text-grey-700" />
                  </figure>
                ))}
              </div>
            ) : null}

            <section className="flex flex-col gap-y-4">
              <Heading as="h2" size="h3" className="text-brand-700">
                Frequently asked questions
              </Heading>
              <div className="divide-y divide-grey-100 rounded-2xl border border-grey-100">
                {story.faq.map(item => (
                  <details key={item.q} className="group px-5 py-4">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                      <Heading as="h3" size="h5">
                        {item.q}
                      </Heading>
                      <span
                        aria-hidden="true"
                        className="text-xl text-brand-500 transition-transform group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <p className="mt-3 text-base leading-7 text-grey-800">
                      {item.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            <section className="flex flex-col gap-y-4 rounded-2xl bg-brand-50 p-6 md:p-8">
              <p className="text-lg leading-7 text-brand-700">
                {story.closing}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  href="/create-ad"
                  className="rounded-full bg-brand-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-600"
                >
                  Sell your {story.name} piece
                </Link>
                <Link
                  href="/brands"
                  className="rounded-full border border-brand-500 px-5 py-2.5 text-sm font-bold text-brand-500 hover:bg-white"
                >
                  Explore all brands
                </Link>
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-grey-100 p-5">
              <Heading as="h2" size="h4" className="mb-4 text-brand-700">
                Iconic {story.name} pieces
              </Heading>
              <ul className="flex flex-col gap-y-4">
                {story.iconicPieces.map(piece => (
                  <li key={piece.name} className="flex flex-col gap-y-1">
                    <span className="font-bold text-black">{piece.name}</span>
                    {piece.designer || piece.year ? (
                      <span className="text-xs uppercase tracking-wide text-grey-600">
                        {[piece.designer, piece.year]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    ) : null}
                    <span className="text-sm leading-6 text-grey-800">
                      {piece.note}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl bg-flavour-50 p-5 text-sm leading-6 text-grey-800">
              Every listing on Whoppah is reviewed by our curation team before
              it goes live.
            </div>
          </aside>
        </div>
      </article>
    </>
  );
};

const Byline = () => (
  <Link
    href="/about/evelien"
    className="flex w-fit items-center gap-x-3 rounded-full pr-4 hover:bg-grey-50"
  >
    <span className="relative size-12 shrink-0 overflow-hidden rounded-full">
      <Image
        src={EvelienAvatar}
        alt="Evelien Bunnik-Remmelts"
        fill
        sizes="48px"
        className="object-cover"
      />
    </span>
    <span className="flex flex-col">
      <span className="text-sm font-bold text-black">
        Written by Evelien Bunnik-Remmelts
      </span>
      <span className="text-xs text-grey-600">Co-Founder and CEO, Whoppah</span>
    </span>
  </Link>
);

const ImageCredit = ({
  image,
  className,
}: {
  image: BrandStory["images"][number];
  className?: string;
}) => (
  <figcaption className={cn("px-2 py-1 text-[0.6875rem] leading-4", className)}>
    Photo:{" "}
    <a
      href={image.sourcePage}
      target="_blank"
      rel="noopener noreferrer nofollow"
      className="underline"
    >
      {image.credit}
    </a>
    , {image.license}, via Wikimedia Commons
  </figcaption>
);
